import * as ReactDOM from 'react-dom';
import {useEffect, useState, useRef} from 'react';
import {Scrollama, Step} from 'react-scrollama';
import {motion} from 'framer-motion';
import {useMediaQuery} from 'usehooks-ts';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';

import type {SanityModuleMap} from '../../types';
import PortableText from '../portableText/PortableText.client';

mapboxgl.accessToken =
  'pk.eyJ1IjoiYnJhdmVtZWRpYSIsImEiOiJjbGg2cjE2c2swOGl1M3BtcmMwdDNmaW83In0.17tbupT7cRJXQky17hOJiA';

export default function MapModule({module}: {module: SanityModuleMap}) {
  const {heading, subheading, points, stats}: any = module;

  const desktop = useMediaQuery('(min-width: 768px)');

  const mapContainer = useRef(null);
  const map = useRef<any>(null);

  useEffect(() => {
    if (map.current) return; // initialize map only once
    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/bravemedia/cm4c65v3w01ea01rddbhd22rl',
      center: desktop
        ? [89.62320624788758, 0.02321440899289269]
        : [150.62320624788758, 0.02321440899289269],
      zoom: 2.15,
      minZoom: 0,
    });
  }, []);

  useEffect(() => {
    if (!map.current) return; // wait for map to initialize
    // disable map zoom when using scroll
    map.current.scrollZoom.disable();
    typeof window !== 'undefined' &&
      window?.innerWidth < 768 &&
      map.current.dragPan.disable();

    for (let i = 0; i < points.length; i++) {
      const element = document.createElement('div');
      ReactDOM.render(
        <div className="h-4 w-4 rounded-full bg-orange blur-sm" />,
        element,
      );

      new mapboxgl.Marker(element)
        .setLngLat([points[i].lng, points[i].lat])
        .addTo(map.current);
    }
  }, []);

  function handleZoom(dir: string) {
    if (dir === 'in') {
      map.current.zoomIn();
    } else {
      map.current.zoomOut();
    }
  }

  //animation setup
  //Scroll trigger
  const [progress, setProgress] = useState<any>(null);

  // This callback fires when a Step hits the offset threshold. It receives the
  // data prop of the step, which in this demo stores the index of the step.

  const onStepProgress = ({progress: localProgress}: any) => {
    setProgress(Math.round((localProgress * 1000) / 10));
  };

  return (
    <div className="relative h-screen lg:h-[220vh]">
      {/* Scroll Tracking */}
      <Scrollama
        offset={0.5}
        debug={false}
        progress
        onStepProgress={onStepProgress}
      >
        <Step data={1} key={1}>
          <div className="absolute top-[50vh] z-30 flex h-screen flex-col items-center justify-center text-orange opacity-0">
            <div>Progress: {progress}%</div>
          </div>
        </Step>
      </Scrollama>
      {/* Content */}
      <div className="sticky top-0 grid h-screen grid-cols-14 items-center justify-start bg-dark-blue py-space-s text-white lg:justify-center lg:py-0">
        <div className="pointer-events-none top-14 z-10 col-start-2 col-end-14 w-full text-center lg:absolute lg:col-start-3 lg:col-end-13">
          {heading && (
            <h3 className="blockH3 mb-5 block font-bold text-orange">
              {heading}
            </h3>
          )}
          <PortableText blocks={subheading} />
        </div>

        {/* Stats */}
        <motion.div
          style={{y: `${75 - progress / 1.5}vh`}}
          className="pointer-events-none absolute z-10 col-start-2 col-end-14 hidden w-full flex-col items-start justify-between gap-x-12 gap-y-4 text-center md:flex-row lg:flex"
        >
          {stats?.map(({value, label}: any, i: number) => (
            <div key={i} className="w-full">
              <span className="font-bold text-[30px] text-orange xl:text-[100px]">
                {Math.round((value / 100) * progress)
                  .toString()
                  .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              </span>
              <h4 className="richH4 font-bold">{label}</h4>
            </div>
          ))}
        </motion.div>

        <div className="relative z-10 col-start-2 col-end-14 flex flex-col items-center justify-center text-center lg:hidden">
          {stats?.map(({value, label}: any, i: number) => (
            <div key={i} className="w-full">
              <span className="font-bold text-[30px] text-orange xl:text-[100px]">
                {value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              </span>
              <h4 className="richH4 font-bold">{label}</h4>
            </div>
          ))}
        </div>

        <div className="absolute left-0 top-0 col-start-1 col-end-15 h-full w-full lg:relative">
          {/* Map */}
          <div
            ref={mapContainer}
            className="col-start-1 col-end-15 min-h-screen w-full md:row-start-1 md:row-end-5"
          />
          {/* Gradient */}
          <div className="pointer-events-none absolute left-0 top-0 h-screen w-full bg-gradient-to-b from-dark-blue to-dark-blue/0" />
          {/* Overlay */}
          <motion.div
            style={{opacity: progress / 100}}
            className="pointer-events-none absolute left-0 top-0 hidden h-screen w-full bg-black/30 lg:block"
          />
          {/* Buttons */}
          <motion.div
            style={{opacity: 1 - progress / 100}}
            className="absolute bottom-16 right-10 hidden flex-col items-center justify-center gap-y-4 lg:flex"
          >
            <button
              onClick={() => handleZoom('in')}
              className="flex h-16 w-16 items-center justify-center rounded-full bg-orange/30"
            >
              <svg
                width="32"
                height="32"
                viewBox="0 0 32 32"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M16.0887 0.167969C15.1447 0.167969 14.3795 0.93314 14.3795 1.87718V14.2701H1.98656C1.04251 14.2701 0.277344 15.0353 0.277344 15.9793C0.277344 16.9234 1.04251 17.6885 1.98656 17.6885H14.3795V30.0815C14.3795 31.0255 15.1447 31.7907 16.0887 31.7907C17.0327 31.7907 17.7979 31.0255 17.7979 30.0815V17.6885H30.1908C31.1349 17.6885 31.9001 16.9234 31.9001 15.9793C31.9001 15.0353 31.1349 14.2701 30.1908 14.2701H17.7979V1.87718C17.7979 0.93314 17.0327 0.167969 16.0887 0.167969Z"
                  fill="#F27021"
                />
              </svg>
            </button>
            <button
              onClick={() => handleZoom('out')}
              className="flex h-16 w-16 items-center justify-center rounded-full bg-orange/30"
            >
              <svg
                width="32"
                height="4"
                viewBox="0 0 32 4"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect width="32" height="4" rx="2" fill="#F27021" />
              </svg>
            </button>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
